import {
  div,
  fnstate,
  goTo,
  h1,
  img,
  pathParameters,
  span
} from '@srfnstack/fntags'
import { lastJobUpdate } from '../../state.js'
import { CreditButton } from '../../components/CreditButton.js'

import { getHex, getSwatch, isLight } from '../../utils/colors.js'

export const ProductInit = () => {
  const productId = pathParameters().productId
  const product = fnstate(null)
  const saving = fnstate(false)

  // previewColor: the color currently shown behind the artwork (click-to-preview)
  const previewColor = fnstate(null)
  // selectedColors: colors chosen for production (max 5)
  const selectedColors = fnstate([])

  const fetchProduct = async () => {
    try {
      const res = await fetch(`/api/product-concepts/${productId}?_=${Date.now()}`)
      if (res.ok) {
        const data = await res.json()
        product(data)
        // Initialize preview to first selected color if available
        if (previewColor() === null) {
          const initial = data.attributes?.selected_colors?.[0] || data.attributes?.available_colors?.[0] || null
          if (initial) previewColor(initial)
        }
        // Seed selectedColors from existing selection if not yet set
        if (selectedColors().length === 0 && data.attributes?.selected_colors?.length > 0) {
          selectedColors(data.attributes.selected_colors)
        }
      }
    } catch (err) {
      console.error('Fetch error:', err)
    }
  }

  let rootEl = null
  const unsubRef = { fn: null }
  unsubRef.fn = lastJobUpdate.subscribe((event) => {
    if (rootEl && !rootEl.isConnected) {
      unsubRef.fn?.()
      unsubRef.fn = null
      return
    }
    const isRelevant = event.payload?.product_concept_id?.toString() === productId.toString()
    if (isRelevant) fetchProduct()
  })

  // Poll for BG_REMOVAL + PRODUCT_INITIALIZE completion so the loading screen
  // resolves even if WebSocket events are missed (connection drop, reconnect).
  let pollTimer = null
  const startPolling = () => {
    if (pollTimer) return
    pollTimer = setInterval(() => {
      if (rootEl && !rootEl.isConnected) { clearInterval(pollTimer); pollTimer = null; return }
      const p = product()
      if (p?.attributes?.available_colors?.length && p.bg_removed_file_path) {
        clearInterval(pollTimer); pollTimer = null; return
      }
      fetchProduct()
    }, 4000)
    // Safety: stop polling after 5 minutes regardless
    setTimeout(() => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null } }, 300000)
  }

  fetchProduct().then(startPolling)

  const toggleSelect = (color) => {
    const sel = selectedColors()
    if (sel.includes(color)) {
      selectedColors(sel.filter(c => c !== color))
    } else if (sel.length < 5) {
      selectedColors([...sel, color])
    }
  }

  const handleConfirm = async () => {
    saving(true)
    const colors = selectedColors()
    try {
      const res = await fetch(`/api/product-concepts/${productId}/confirm-colors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_colors: colors })
      })
      if (res.ok) {
        goTo(`/product-review/${productId}`)
      } else {
        const err = await res.json()
        await window.alert('Failed to confirm colors: ' + (err.error || 'Unknown error'))
        saving(false)
      }
    } catch (e) {
      await window.alert('Unexpected error: ' + e.message)
      saving(false)
    }
  }

  const LoadingScreen = () =>
    div({ class: 'flex flex-col items-center justify-center flex-1 py-32 gap-6' },
      span({ class: 'loading loading-spinner loading-lg text-primary' }),
      div({ class: 'text-center space-y-1' },
        div({ class: 'font-bold text-lg' }, 'Preparing your artwork\u2026'),
        div({ class: 'text-sm opacity-50' }, 'Removing background. This usually takes a few seconds.')
      )
    )

  const ColorCircle = (color) => {
    const hex = getHex(color)
    const swatch = getSwatch(color)
    const light = isLight(hex)

    return div({ class: 'flex flex-col items-center gap-1' },
      // Main circle: click = preview
      previewColor.bindAs(pc => {
        const isPreviewing = pc === color
        return div({
          class: `w-11 h-11 rounded-full cursor-pointer transition-all duration-150 flex items-center justify-center bg-cover ${light ? 'border border-base-content/20' : ''} ${isPreviewing ? 'ring-4 ring-primary ring-offset-2 ring-offset-base-100 scale-110' : 'hover:scale-105'}`,
          style: `background-color: ${hex}${swatch ? `; background-image: url('${swatch}'); background-blend-mode: multiply` : ''}`,
          onclick: () => previewColor(color),
          onmouseenter: () => previewColor(color)
        })
      }),
      // Checkbox below: click = toggle production selection
      selectedColors.bindAs(sel => {
        const isSelected = sel.includes(color)
        const atLimit = sel.length >= 5 && !isSelected
        return span({
          class: `material-symbols-rounded cursor-pointer transition-colors duration-100 text-[24px] ${atLimit ? 'opacity-25 pointer-events-none' : 'opacity-70 hover:opacity-100'} ${isSelected ? 'text-primary' : ''}`,
          onclick: (e) => { e.stopPropagation(); toggleSelect(color) },
          title: isSelected ? 'Remove from selection' : 'Add to selection'
        }, isSelected ? 'check_box' : 'check_box_outline_blank')
      }),
      span({ class: 'text-[9px] text-center leading-tight max-w-[48px] opacity-60' }, color)
    )
  }

  const ReadyScreen = (p) => {
    const availableColors = p.attributes?.available_colors || []
    const bgRemovedPath = p.bg_removed_file_path

    return div({ class: 'flex flex-col items-center gap-8 py-10 px-4 w-full max-w-3xl mx-auto' },

      h1({ class: 'text-2xl font-bold text-center' }, 'Choose Shirt Colors'),
      div({ class: 'text-sm opacity-50 text-center -mt-5' }, 'Click a color to preview. Check the box below each to include it in your product (up to 5).'),

      // Preview area
      div({ class: 'relative w-full max-w-lg mx-auto rounded-2xl overflow-hidden shadow-xl border border-base-300 aspect-square' },
        // Checkerboard layer (transparent indicator)
        div({
          class: 'absolute inset-0',
          style: 'background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0 / 24px 24px'
        }),
        // Solid color layer driven by previewColor
        previewColor.bindAs(pc => {
          const pcSwatch = pc ? getSwatch(pc) : null
          return div({
            class: 'absolute inset-0 transition-colors duration-200 bg-repeat',
            style: pc ? `background-color: ${getHex(pc)}${pcSwatch ? `; background-image: url('${pcSwatch}'); background-blend-mode: multiply` : ''}` : ''
          })
        }),
        // Transparent artwork on top
        bgRemovedPath
          ? img({
            class: 'absolute inset-0 w-full h-full object-contain p-6',
            src: `/api/image?file=${encodeURIComponent(bgRemovedPath)}`,
            decoding: 'async'
          })
          : div({ class: 'absolute inset-0 flex items-center justify-center opacity-30' },
            span({ class: 'material-symbols-rounded text-6xl' }, 'image')
          )
      ),

      // Color palette strip
      availableColors.length > 0
        ? div({ class: 'flex flex-wrap gap-x-4 gap-y-5 justify-center' },
          availableColors.map(c => ColorCircle(c))
        )
        : div({ class: 'text-sm opacity-50' }, 'No colors available for this product.'),

      // Selected colors chips
      selectedColors.bindAs(sel =>
        sel.length > 0
          ? div({ class: 'flex flex-wrap gap-2 justify-center' },
            sel.map(c => {
              const hex = getHex(c)
              const sw = getSwatch(c)
              const light = isLight(hex)
              return div({
                class: `flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full border text-xs font-medium cursor-pointer ${light ? 'border-base-content/20' : 'border-transparent'} bg-base-200`,
                onclick: () => toggleSelect(c)
              },
              div({
                class: `w-3.5 h-3.5 rounded-full flex-shrink-0 bg-cover ${light ? 'border border-base-content/20' : ''}`,
                style: `background-color: ${hex}${sw ? `; background-image: url('${sw}'); background-blend-mode: multiply` : ''}`
              }),
              span({}, c),
              span({ class: 'material-symbols-rounded !text-[14px] opacity-50 hover:opacity-100' }, 'close')
              )
            })
          )
          : div({ class: 'text-sm opacity-40 italic' }, 'No colors selected yet')
      ),

      // Confirm button
      saving.bindAs(isSaving =>
        CreditButton({
          actionCode: 'MOCKUP_GENERATE',
          class: 'btn btn-primary btn-lg gap-2',
          onclick: handleConfirm,
          disabled: isSaving || selectedColors.bindAttr(sel => sel.length === 0),
          label: span({ class: 'flex items-center gap-2' },
            isSaving
              ? span({ class: 'loading loading-spinner loading-sm' })
              : span({ class: 'material-symbols-rounded' }, 'rocket_launch'),
            'Create Product'
          ),
          multipliers: { count: 6 }
        })
      )
    )
  }

  rootEl = div({ class: 'flex flex-col min-h-[calc(100vh-4rem)] p-4' },
    product.bindAs(p => {
      if (!p) return LoadingScreen()
      // Still processing (waiting for PRODUCT_INITIALIZE + BG_REMOVAL to complete)
      if (!p.attributes?.available_colors?.length || !p.bg_removed_file_path) {
        return LoadingScreen()
      }
      return ReadyScreen(p)
    })
  )

  return rootEl
}
